
import { useState, useCallback, useRef, DragEvent } from "react";

export interface DragDropState {
  isDragging: boolean;
  isOver: boolean;
}

export function useDragDrop(onDrop: (files: FileList) => void) {
  const [state, setState] = useState<DragDropState>({
    isDragging: false,
    isOver: false,
  });

  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current++;

    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setState((prev) => ({ ...prev, isDragging: true }));
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current--;

    if (dragCounter.current === 0) {
      setState({ isDragging: false, isOver: false });
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setState((prev) => ({ ...prev, isOver: true }));
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setState({ isDragging: false, isOver: false });
      dragCounter.current = 0;

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        onDrop(e.dataTransfer.files);
        e.dataTransfer.clearData();
      }
    },
    [onDrop]
  );

  const reset = useCallback(() => {
    setState({ isDragging: false, isOver: false });
    dragCounter.current = 0;
  }, []);

  return {
    ...state,
    handlers: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    reset,
  };
}
